/** Stockage Editor — React Query hooks */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
    MockChiller,
    MockPlant,
    MockPowerContract,
    MockPump,
    MockCoolingSystem,
    MockStorageCentral,
    MockStorageNetwork,
    StorageCentralFormData,
    StorageNetworkFormData,
} from './types';

// ── Query keys ──────────────────────────────────────────────────────────

const QUERY_KEYS = {
    plants: ['stockage-editor', 'plants'] as const,
    chillers: ['stockage-editor', 'chillers'] as const,
    pumps: ['stockage-editor', 'pumps'] as const,
    powerContracts: ['stockage-editor', 'power-contracts'] as const,
    coolingSystems: ['stockage-editor', 'cooling-systems'] as const,
    storagesCentral: ['stockage-editor', 'storages-central'] as const,
    storagesNetwork: ['stockage-editor', 'storages-network'] as const,
};

// ── Queries ─────────────────────────────────────────────────────────────

export function usePlants() {
    return useQuery<MockPlant[]>({
        queryKey: QUERY_KEYS.plants,
        queryFn: async () => {
            const response = await fetch('/api/stockage-editor/plants');
            if (!response.ok) throw new Error('Failed to fetch plants');
            return response.json();
        },
    });
}

export function useChillers() {
    return useQuery<MockChiller[]>({
        queryKey: QUERY_KEYS.chillers,
        queryFn: async () => {
            const response = await fetch('/api/stockage-editor/chillers');
            if (!response.ok) throw new Error('Failed to fetch chillers');
            return response.json();
        },
    });
}

export function usePumps() {
    return useQuery<MockPump[]>({
        queryKey: QUERY_KEYS.pumps,
        queryFn: async () => {
            const response = await fetch('/api/stockage-editor/pumps');
            if (!response.ok) throw new Error('Failed to fetch pumps');
            return response.json();
        },
    });
}

export function usePowerContracts() {
    return useQuery<MockPowerContract[]>({
        queryKey: QUERY_KEYS.powerContracts,
        queryFn: async () => {
            const response = await fetch('/api/stockage-editor/power-contracts');
            if (!response.ok) throw new Error('Failed to fetch power contracts');
            return response.json();
        },
    });
}

export function useCoolingSystems() {
    return useQuery<MockCoolingSystem[]>({
        queryKey: QUERY_KEYS.coolingSystems,
        queryFn: async () => {
            const response = await fetch('/api/stockage-editor/cooling-systems');
            if (!response.ok) throw new Error('Failed to fetch cooling systems');
            return response.json();
        },
    });
}

export function useStoragesCentral() {
    return useQuery<MockStorageCentral[]>({
        queryKey: QUERY_KEYS.storagesCentral,
        queryFn: async () => {
            const response = await fetch('/api/stockage-editor/storages-central');
            if (!response.ok) throw new Error('Failed to fetch central storages');
            return response.json();
        },
    });
}

export function useStoragesNetwork() {
    return useQuery<MockStorageNetwork[]>({
        queryKey: QUERY_KEYS.storagesNetwork,
        queryFn: async () => {
            const response = await fetch('/api/stockage-editor/storages-network');
            if (!response.ok) throw new Error('Failed to fetch network storages');
            return response.json();
        },
    });
}

// ── Mutations ───────────────────────────────────────────────────────────

export function useCreateChiller() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: Partial<MockChiller>) => {
            const response = await fetch('/api/stockage-editor/chillers', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to create chiller');
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.chillers });
        },
    });
}

export function useUpdateNode() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ nodeId, data }: { nodeId: string; data: Record<string, unknown> }) => {
            const response = await fetch(`/api/stockage-editor/nodes/${nodeId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to update node');
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.chillers });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pumps });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.powerContracts });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.storagesCentral });
        },
    });
}

export function useDeleteNode() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (nodeId: string) => {
            const response = await fetch(`/api/stockage-editor/nodes/${nodeId}`, {
                method: 'DELETE',
            });
            if (!response.ok) throw new Error('Failed to delete node');
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.chillers });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pumps });
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.storagesCentral });
        },
    });
}

// ── Stockages centraux ──────────────────────────────────────────────────

export function useCreateStorageCentral() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: StorageCentralFormData) => {
            const response = await fetch('/api/stockage-editor/storages-central', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to create central storage');
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.storagesCentral });
        },
    });
}

export function useUpdateStorageCentral() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, data }: { id: number; data: Partial<StorageCentralFormData> }) => {
            const response = await fetch(`/api/stockage-editor/storages-central/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to update central storage');
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.storagesCentral });
        },
    });
}

export function useDeleteStorageCentral() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const response = await fetch(`/api/stockage-editor/storages-central/${id}`, {
                method: 'DELETE',
            });
            if (!response.ok) throw new Error('Failed to delete central storage');
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.storagesCentral });
        },
    });
}

// ── Stockages réseau ────────────────────────────────────────────────────

export function useCreateStorageNetwork() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: StorageNetworkFormData) => {
            const response = await fetch('/api/stockage-editor/storages-network', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to create network storage');
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.storagesNetwork });
        },
    });
}

export function useUpdateStorageNetwork() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: Partial<StorageNetworkFormData> }) => {
            const response = await fetch(`/api/stockage-editor/storages-network/${id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
            });
            if (!response.ok) throw new Error('Failed to update network storage');
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.storagesNetwork });
        },
    });
}

export function useDeleteStorageNetwork() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            const response = await fetch(`/api/stockage-editor/storages-network/${id}`, {
                method: 'DELETE',
            });
            if (!response.ok) throw new Error('Failed to delete network storage');
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.storagesNetwork });
        },
    });
}
